import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/a11y';
import SupportAccordion from './SupportAccordion';
import SupportButtonTypeLinks from './SupportButtonTypeLinks';
import SupportLinksWithThumbnails from './SupportLinksWithThumbnails';
import SupportTextEditor from './SupportTextEditor';
import SupportSlider from './SupportSlider';
import SupportTwoColumnsLayout from './SupportTwoColumnsLayout';

interface SupportTabProps {
    element: any
}
interface TabItem {
    id: number;
    label: string;
    content: any;
  }

const SupportTab: React.FC<SupportTabProps> = (element) => {
    const swiperRef = useRef<any>(null);
    const [active, setActive] = useState<number>(0);
    const [items, setItems] = useState<TabItem[]>([]);
    const [TabContent, setTabContent] = useState<any[]>([]);
    const Content: React.SetStateAction<any[]> = [];
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const userAgent = navigator.userAgent.toLowerCase();
        const mobile = /iphone|ipad|ipod|android/i.test(userAgent);
        setIsMobile(mobile);
    }, []);

    useEffect(() => {
      const newItems: TabItem[] = [];
      element?.element?.TabItems?.data.forEach((element: any, index: number) => {
        newItems.push({ id: index, label: element?.attributes?.TabTitle, content: element?.attributes?.TabContent});
      });
      setItems(newItems);
    }, [element]);

    useEffect(()=>{
        handleTabContent(items, active);
    },[items])

    const handleTabContent = (item: any, active: number) => {
        setTabContent([]);
        item[active]?.content?.forEach((element:  {__component: string}, index: number) => {
            switch(element?.__component){
                case "support.text-editor":
                    Content.push(<SupportTextEditor element={element} key={index}/>);
                    break;
                case "support.button-type-links":
                    Content.push(<SupportButtonTypeLinks element={element}  key={index}/>);
                    break;
                case "support.links-with-thumbnails":
                    Content.push(<SupportLinksWithThumbnails element={element}  key={index}/>);
                    break;
                case "support.accordion":
                    Content.push(<SupportAccordion element={element}  key={index}/>);
                    break;
                case "support.slider":
                    Content.push(<SupportSlider element={element} key={index}/>);
                    break;
                case "support.two-columns-layout":
                    Content.push(<SupportTwoColumnsLayout element={element} key={index}/>);
                    break;
            }
        });
        setTabContent(Content);
    }
  
    const swiperConfig = useMemo(() => {
        let config: any = {};
        switch(parseInt(items.length.toString())) {
            case 1:
                config = {
                    navigation: {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    },
                    modules: [Navigation, Pagination, Scrollbar, A11y],
                    onSwiper: (swiper: any) => {
                        swiperRef.current = swiper;
                    },
                }
            break;
            case 2:
                config = {
                    slidesPerView: 1,
                    spaceBetween: 8,
                    observer: true,
                    observeParents: true,
                    navigation: {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    },
                    breakpoints: {
                        640: {
                            slidesPerView: 2,
                            spaceBetween: 10,
                            slidesPerGroup: 2
                        },
                        480: {
                            slidesPerView: 1,
                            spaceBetween: 8,
                            allowTouchMove: true,
                        }
                    },
                    modules: [Navigation, Pagination, Scrollbar, A11y],
                    onSwiper: (swiper: any) => {
                        swiperRef.current = swiper;
                    },
                }
            break;
            case 3:
                config = {
                    slidesPerView: 1,
                    spaceBetween: 8,
                    observer: true,
                    observeParents: true,
                    navigation: {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    },
                    breakpoints: {
                        991: {
                            slidesPerView: 3,
                            spaceBetween: 10,
                            allowTouchMove: false,
                        },
                        797: {
                            slidesPerView: 3,
                            spaceBetween: 10,
                            slidesPerGroup: 2
                        },
                        640: {
                            slidesPerView: 2,
                            spaceBetween: 10,
                            slidesPerGroup: 2
                        },
                        480: {
                            slidesPerView: 1,
                            spaceBetween: 8,
                            slidesPerGroup: 1
                        }
                    },
                    modules: [Navigation, Pagination, Scrollbar, A11y],
                    onSwiper: (swiper: any) => {
                    swiperRef.current = swiper;
                    },    
                }
            break;
            default:
                config = {
                    slidesPerView: 1,
                    spaceBetween: 8,
                    slidesPerGroup: 1,
                    observer: true,
                    observeParents: true,
                    navigation: {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    },
                    breakpoints: {
                        991: {
                            slidesPerView: 6,
                            spaceBetween: 10,
                            slidesPerGroup: 4
                        },
                        797: {
                            slidesPerView: 3,
                            spaceBetween: 10,
                            slidesPerGroup: 2
                        },
                        640: {
                            slidesPerView: 2,
                            spaceBetween: 10,
                            slidesPerGroup: 2
                        },
                        480: {
                            slidesPerView: 1,
                            spaceBetween: 8,
                            slidesPerGroup: 1
                        }
                    },
                modules: [Navigation, Pagination, Scrollbar, A11y],
                onSwiper: (swiper: any) => {
                    swiperRef.current = swiper;
                },
                }
            break;
        }
        return config;
    }, [items]);

    const handleClick = (itemKey: number) => {
        setActive(itemKey);
        handleTabContent(items, itemKey);
    };

    return (
      <>
        <div className="support-tab spacer-top tab-carousel-wrap">
            <div className="container-fluid">
                <div className="swiper-container tab-carousel">
                    <Swiper key={items} {...swiperConfig}>
                        {items.map((item: any, index: number) => (
                            <SwiperSlide key={index}>
                                <div 
                                className={`item text-center hand-cursor ${isMobile ? "tab-item-m" : "tab-item"} ${active === item?.id ? 'active' : ''}`}
                                data-target={`tab-${item?.id}`}
                                onClick={() => handleClick(item?.id)}>
                                    <span className="py-2 d-inline-block">{item?.label}</span>
                                </div>
                            </SwiperSlide>
                        ))}
                        <div className="swiper-button-next"></div>
                        <div className="swiper-button-prev"></div>
                    </Swiper>
                </div>
            </div>
        </div>
        {TabContent ? TabContent : <></>}
      </>
    );
};

export default SupportTab;